import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, CheckCircle, Users, FileCheck, AlertTriangle, Lock, Eye, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const Safety = () => {
  const appUrl = process.env.REACT_APP_EXTERNAL_APP_URL || 'https://aupairshare.base44.app';

  const handleAppRedirect = () => {
    window.open(appUrl, '_blank');
  };

  const safetyPillars = [
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Background Checks',
      description: 'Comprehensive criminal background checks for all au pairs',
      details: [
        'Police clearance verification',
        'Identity document validation',
        'Reference checks from previous employers',
        'Driving record verification',
        'Psychometric assessments to understand personality and values'
      ]
    },
    {
      icon: <FileCheck className="w-12 h-12" />,
      title: 'Qualifications Verification',
      description: 'Confirmation of student enrollment or childcare qualifications',
      details: [
        'Student registration verification (for students)',
        'Childcare certifications validation (for professionals)',
        'Academic or professional credentials confirmation',
        'Regular status checks'
      ]
    },
    {
      icon: <Eye className="w-12 h-12" />,
      title: 'Live Tracking',
      description: 'Real-time GPS monitoring during all bookings',
      details: [
        'Track location throughout booking',
        'Route transparency and visibility',
        'Estimated arrival times',
        'Journey history and records'
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Ratings & Accountability',
      description: 'Community-driven trust and feedback system',
      details: [
        'Family ratings and reviews',
        'Au pair performance tracking',
        'Transparent feedback system',
        'Quality assurance monitoring'
      ]
    }
  ];

  const complianceAreas = [
    {
      icon: <FileCheck className="w-8 h-8" />,
      title: 'Children\'s Act Alignment',
      description: 'Full compliance with South African Children\'s Act regulations governing childcare services and child protection.'
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: 'POPIA Compliance',
      description: 'Protection of Personal Information Act adherence — your family\'s data is secure and private.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Transport Regulations',
      description: 'Compliance with vehicle and driver requirements for child transportation services.'
    }
  ];

  const incidentResponse = [
    {
      icon: <AlertTriangle className="w-8 h-8" />,
      title: 'Incident Reporting',
      description: 'Built-in reporting system for any concerns or issues during bookings.'
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: 'Support Team',
      description: 'Dedicated support available to address safety concerns immediately.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Investigation Process',
      description: 'Thorough review and action on all reported incidents.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="Safety & Compliance"
        description="Au Pair Share's comprehensive safety measures: background checks, psychometric assessments, POPIA compliance, and real-time tracking. Your child's safety is our priority."
        keywords="safe childcare, vetted au pairs, background checks, POPIA compliant, child safety South Africa"
        path="/safety"
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-50 opacity-50"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="bg-slate-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto text-white">
              <Shield className="w-12 h-12" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              Safety & Compliance
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Your child's safety is our absolute priority. Multiple layers of protection, verification, and accountability.
            </p>
          </div>
        </div>
      </section>

      {/* Main Safety Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Our Safety Framework
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive protection at every step
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {safetyPillars.map((pillar, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 space-y-6">
                  <div className="bg-slate-100 w-16 h-16 rounded-xl flex items-center justify-center text-slate-700">
                    {pillar.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-slate-900 mb-2">{pillar.title}</h3>
                    <p className="text-slate-600 mb-4">{pillar.description}</p>
                  </div>
                  <ul className="space-y-2">
                    {pillar.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-600">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Compliance */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Legal Compliance
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Fully compliant with South African regulations
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {complianceAreas.map((area, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="bg-slate-700 w-14 h-14 rounded-lg flex items-center justify-center text-white mb-4">
                  {area.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{area.title}</h3>
                <p className="text-slate-600 leading-relaxed">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Incident Reporting */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Incident Reporting & Response
              </h2>
              <p className="text-xl text-slate-600">
                Immediate support when you need it
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {incidentResponse.map((item, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-8 text-center space-y-4">
                  <div className="bg-slate-700 w-14 h-14 rounded-lg flex items-center justify-center mx-auto text-white">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Image Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-slate-900">
                Built on Trust, Backed by Process
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Every au pair goes through rigorous vetting before they can accept bookings. This includes comprehensive background checks, identity verification, driving records, reference checks, and psychometric assessments to understand their personality, values, and compatibility with childcare. Every family interaction is tracked, rated, and monitored. We do not compromise when it comes to your child's wellbeing.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our psychometric assessments help families understand who their au pair is beyond just credentials — revealing traits like responsibility, empathy, patience, and communication style. For families who care about deeper compatibility, these insights provide valuable peace of mind.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our platform combines technology with human oversight to create a childcare experience that's transparent, accountable, and safe.
              </p>
              <Button 
                size="lg"
                onClick={handleAppRedirect}
                className="bg-slate-700 hover:bg-slate-800 text-white"
              >
                Explore Safety Features in the App
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1659184619594-ef7e655b843e" 
                alt="Au pair reading with child"
                className="w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Additional Safeguards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Additional Safeguards
            </h2>
            <div className="bg-slate-50 rounded-xl p-8 lg:p-12 space-y-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Communication Records</h3>
                  <p className="text-slate-600">All in-app communications are logged for safety and accountability.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Regular Audits</h3>
                  <p className="text-slate-600">Ongoing monitoring and periodic re-verification of all au pairs.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Emergency Protocols</h3>
                  <p className="text-slate-600">Clear procedures for handling emergencies during bookings.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-4xl font-bold">
              Questions About Safety?
            </h2>
            <p className="text-xl text-slate-300">
              Learn more about our safety protocols and compliance measures in the app, or contact our support team directly.
            </p>
            <Button 
              size="lg"
              onClick={handleAppRedirect}
              className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg mt-4"
            >
              Open the App
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Safety;
